import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../i18n/useP';
import Card from '../components/Card';
import PageHeader from '../components/PageHeader';
import {
  listCreditLines,
  createCreditLine, updateCreditLine, deleteCreditLine,
} from '../api/creditLines';
import './CreditLines.css';

/* Autres paramètres — Lignes de crédit (découvert autorisé) à PÉRIODE.
   Chaque ligne : montant + mois de début + mois de fin (optionnel = en cours).
   Les lignes actives se cumulent ; la limite mensuelle effective (récap) est
   dérivée côté serveur et sert de plancher au graphe de trésorerie. */

export default function CashActualsPage({ activeTenant, addToast, year, embedded }) {
  const { t } = useTranslation();
  const p = useP();
  const tenantId = activeTenant?.id;
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const years = Array.from({ length: 11 }, (_, i) => (year - 3) + i);

  const reload = useCallback(() => {
    if (!tenantId) { setLines([]); return; }
    setLoading(true);
    listCreditLines(tenantId)
      .then((l) => { if (!l.error) setLines(l.data || []); })
      .catch(() => addToast && addToast(t('phrases:p_bb8c4e1a2c'), 'error'))
      .finally(() => setLoading(false));
  }, [tenantId, year]); // eslint-disable-line
  useEffect(() => { reload(); }, [reload]);

  async function act(promise) {
    const r = await promise;
    if (r.error) addToast && addToast(r.error, 'error'); else reload();
  }
  const patch = (id, data) => act(updateCreditLine(tenantId, id, data));

  if (!tenantId) {
    return <main className="page-table-layout"><div className="page-table-empty">{p('Sélectionne une entreprise.')}</div></main>;
  }

  const addAction = (
    <button
      type="button"
      className="cl-add"
      onClick={() => act(createCreditLine(tenantId, { amount: 0, start_year: year, start_month: 1 }))}
    >{p('+ Ajouter une ligne de crédit')}</button>
  );

  return (
    <main className="page-table-layout is-fluid">
      {!embedded && <PageHeader title={t('phrases:p_4c28ed46a3')} />}
      <Card
        className="report-card"
        title={t('phrases:p_e07f1a0d76')}
        subtitle={t('phrases:p_11b75112b7')}
      >
        <div className="cl-wrap">
          {loading && <p className="page-table-loading">{p('Chargement…')}</p>}
          {!loading && lines.length === 0 && (
            <p className="cl-empty">{p('Aucune ligne de crédit. « + Ajouter » pour en créer une.')}</p>
          )}
          {!loading && lines.length > 0 && (
            <table className="cl-table">
              <thead>
                <tr>
                  <th className="cl-lbl">{t('phrases:p_f5485bbae8')}</th>
                  <th className="cl-num">{p('Montant (CHF)')}</th>
                  <th>{t('phrases:p_f0955314c5')}</th>
                  <th>{p('Fin')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.id}>
                    <td className="cl-lbl">
                      <input
                        className="cl-input" placeholder={p('(optionnel)')}
                        defaultValue={l.label || ''}
                        onBlur={(e) => { const v = e.target.value.trim(); if (v !== (l.label || '')) patch(l.id, { label: v || null }); }}
                      />
                    </td>
                    <td className="cl-num">
                      <input
                        className="cl-input cl-amount" inputMode="numeric"
                        defaultValue={l.amount ? Math.round(l.amount) : ''}
                        onBlur={(e) => {
                          const v = parseFloat(String(e.target.value).replace(/[^0-9.-]/g, '')) || 0;
                          if (v !== Number(l.amount)) patch(l.id, { amount: v });
                        }}
                      />
                    </td>
                    <td>
                      <MonthYear
                        m={l.start_month} y={l.start_year} years={years}
                        onChange={(mm, yy) => patch(l.id, { start_month: mm, start_year: yy })}
                      />
                    </td>
                    <td>
                      <MonthYear
                        m={l.end_month} y={l.end_year} years={years} allowOpen
                        onChange={(mm, yy) => patch(l.id, mm == null ? { clear_end: true } : { end_month: mm, end_year: yy })}
                      />
                    </td>
                    <td className="cl-del">
                      <button type="button" title={p('Supprimer')} onClick={() => act(deleteCreditLine(tenantId, l.id))}>×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!loading && <div className="cl-addrow">{addAction}</div>}
        </div>
      </Card>
    </main>
  );
}

// Sélecteur mois + année. allowOpen : ajoute « en cours » (mois vide → onChange(null)).
function MonthYear({ m, y, years, allowOpen, onChange }) {
  const { t } = useTranslation();
  const p = useP();
  const months = useMemo(
    () => Array.from({ length: 12 }, (_, i) => t(`common:months.${i + 1}`)),
    [t],
  );
  const open = allowOpen && (m == null);
  return (
    <span className="cl-my">
      <select
        className="cl-select"
        value={open ? '' : String(m)}
        onChange={(e) => {
          const val = e.target.value;
          if (val === '') { onChange(null, null); return; }
          onChange(Number(val), y || years[3]);
        }}
      >
        {allowOpen && <option value="">{p('en cours')}</option>}
        {months.map((lbl, i) => <option key={i} value={i + 1}>{lbl}</option>)}
      </select>
      {!open && (
        <select
          className="cl-select cl-select--year"
          value={String(y || years[3])}
          onChange={(e) => onChange(m || 1, Number(e.target.value))}
        >
          {years.map((yy) => <option key={yy} value={yy}>{yy}</option>)}
        </select>
      )}
    </span>
  );
}
